/*  LessonEditor — right panel of the Course Builder.
    ────────────────────────────────────────────────────────────
    Renders the lesson list for the active module + an editor
    for the selected lesson. Editor swaps by lesson type:

      • video  → title + external URL (YouTube/Vimeo/Loom/GDrive)
      • text   → title + rich HTML textarea
      • file   → title + PDF/DOC uploader
      • quiz   → title + <QuizBuilder />

    Saves with a debounced auto-save on blur + explicit "Save lesson" button.
*/
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
    Plus, Trash, VideoCamera, Article, FileArrowUp, Question, ArrowRight,
    CaretUp, CaretDown, FloppyDisk, WarningCircle, Books, Clock, SpeakerHigh,
} from "@phosphor-icons/react";
import { api, API } from "@/lib/api";
import { confirmDialog } from "@/lib/confirm";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import QuizBuilder from "@/components/creator/QuizBuilder";
import RichTextEditor from "@/components/creator/RichTextEditor";
import NarrationEditor from "@/components/creator/NarrationEditor";


const TYPE_META = {
    video:    { label: "Video",     Icon: VideoCamera, tone: "bg-sky-50 text-sky-700 border-sky-200" },
    text:     { label: "Text",      Icon: Article,     tone: "bg-violet-50 text-violet-700 border-violet-200" },
    narrated: { label: "Narrated",  Icon: SpeakerHigh, tone: "bg-teal-50 text-teal-700 border-teal-200" },
    file:     { label: "File",      Icon: FileArrowUp, tone: "bg-amber-50 text-amber-700 border-amber-200" },
    quiz:     { label: "Quiz",      Icon: Question,    tone: "bg-emerald-50 text-emerald-700 border-emerald-200" },
};


export default function LessonEditor({ course, module, readOnly, onLessonsChange }) {
    const [lessons, setLessons] = useState([]);
    const [activeLessonId, setActiveLessonId] = useState(null);
    const [loading, setLoading] = useState(true);

    // Track only the id (stable string) — see load() below.
    const moduleId = module?.id || null;

    const load = useCallback(async () => {
        // iter100.113q — Bug fix: this useCallback previously depended on
        // the entire `module` object. Every time we called
        // `onLessonsChange` at the end of load, the parent's
        // `setModules` handed us a fresh `activeModule` reference, which
        // re-created `load`, which re-fired this effect → infinite
        // "Loading…" flicker in the lesson list column. Depend on the
        // stable `moduleId` string instead so this only runs when the
        // active module actually changes.
        if (!moduleId) { setLessons([]); setLoading(false); return; }
        setLoading(true);
        try {
            const { data } = await api.get(`/course-builder/modules/${moduleId}/lessons`);
            setLessons(data || []);
            setActiveLessonId((prev) => {
                if (prev && (data || []).some((l) => l.id === prev)) return prev;
                return data?.[0]?.id || null;
            });
            onLessonsChange?.(moduleId, data || []);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't load lessons.");
        } finally {
            setLoading(false);
        }
    }, [moduleId, onLessonsChange]);

    useEffect(() => { load(); }, [load]);

    const activeLesson = useMemo(() => lessons.find((l) => l.id === activeLessonId) || null, [lessons, activeLessonId]);

    const addLesson = async (type) => {
        try {
            const { data } = await api.post(`/course-builder/modules/${module.id}/lessons`, {
                title: `New ${TYPE_META[type].label} lesson`,
                type,
            });
            const next = [...lessons, data];
            setLessons(next);
            setActiveLessonId(data.id);
            onLessonsChange?.(module.id, next);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't add lesson.");
        }
    };

    const persistLesson = async (lessonId, patch) => {
        try {
            const { data } = await api.put(`/course-builder/lessons/${lessonId}`, patch);
            const next = lessons.map((l) => (l.id === lessonId ? { ...l, ...data } : l));
            setLessons(next);
            onLessonsChange?.(module.id, next);
            return data;
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't save lesson.");
            throw e;
        }
    };

    // iter100.157n — used by NarrationEditor to publish a fresh lesson doc
    // (with narration_* fields) without an extra round-trip.
    const applyLessonRefresh = async (freshLesson) => {
        if (freshLesson && freshLesson.id) {
            const next = lessons.map((l) => (l.id === freshLesson.id ? { ...l, ...freshLesson } : l));
            setLessons(next);
            onLessonsChange?.(module.id, next);
            return;
        }
        // Fallback: reload from server (e.g. after DELETE)
        await load();
    };

    const removeLesson = async (l) => {
        const ok = await confirmDialog({
            title: "Delete lesson?",
            description: `"${l.title}" will be removed. This cannot be undone.`,
            confirmLabel: "Delete",
            variant: "destructive",
        });
        if (!ok) return;
        try {
            await api.delete(`/course-builder/lessons/${l.id}`);
            const next = lessons.filter((row) => row.id !== l.id);
            setLessons(next);
            if (activeLessonId === l.id) setActiveLessonId(next[0]?.id || null);
            onLessonsChange?.(module.id, next);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't delete lesson.");
        }
    };

    const moveLesson = async (idx, dir) => {
        const to = idx + dir;
        if (to < 0 || to >= lessons.length) return;
        const reordered = [...lessons];
        [reordered[idx], reordered[to]] = [reordered[to], reordered[idx]];
        setLessons(reordered.map((l, i) => ({ ...l, order: i })));
        onLessonsChange?.(module.id, reordered);
        try {
            await api.post(`/course-builder/modules/${module.id}/lessons/reorder`, {
                items: reordered.map((l, i) => ({ id: l.id, order: i })),
            });
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't reorder.");
        }
    };

    if (!module) {
        return (
            <div className="flex-1 flex items-center justify-center p-10">
                <div className="text-center">
                    <Books size={40} weight="duotone" className="mx-auto text-muted-foreground mb-3" />
                    <div className="text-sm font-semibold">Pick a module to begin</div>
                    <p className="text-xs text-muted-foreground mt-1">Select a module on the left, or add a new one.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="flex-1 flex flex-col min-h-0" data-testid="lesson-editor">
            {/* Module header + add-lesson toolbar */}
            <div className="px-6 py-4 border-b border-border bg-card">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div>
                        <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Module</div>
                        <h2 className="font-display text-xl font-bold tracking-tight" data-testid="lesson-editor-module-title">
                            {module.title}
                        </h2>
                    </div>
                    {!readOnly && (
                        <div className="flex items-center gap-1.5 flex-wrap" data-testid="lesson-add-toolbar">
                            <span className="text-[11px] font-semibold text-muted-foreground mr-1">Add lesson:</span>
                            {Object.entries(TYPE_META).map(([key, meta]) => (
                                <button
                                    key={key}
                                    type="button"
                                    onClick={() => addLesson(key)}
                                    className="inline-flex items-center gap-1 h-8 px-2.5 rounded-md border border-border bg-secondary text-xs font-bold hover:bg-primary/10 hover:border-primary/40 hover:text-primary transition-colors"
                                    data-testid={`lesson-add-${key}`}
                                >
                                    <meta.Icon size={11} weight="fill" /> {meta.label}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Lesson strip + editor */}
            <div className="flex flex-1 min-h-0">
                {/* Lesson list (compact left rail) */}
                <div className="w-56 shrink-0 border-r border-border bg-secondary/30 overflow-y-auto p-2" data-testid="lesson-list">
                    {loading ? (
                        <div className="text-xs text-muted-foreground text-center py-6">Loading…</div>
                    ) : lessons.length === 0 ? (
                        <div className="text-xs text-muted-foreground text-center py-6">
                            {readOnly ? "No lessons in this module." : "Add your first lesson from the toolbar above."}
                        </div>
                    ) : (
                        lessons.map((l, idx) => {
                            const meta = TYPE_META[l.type] || TYPE_META.text;
                            const isActive = l.id === activeLessonId;
                            return (
                                <div
                                    key={l.id}
                                    className={`rounded-lg mb-1 border transition-all ${
                                        isActive
                                            ? "bg-primary/5 border-primary/30"
                                            : "border-transparent hover:bg-white"
                                    }`}
                                    data-testid={`lesson-row-${l.id}`}
                                >
                                    <button
                                        type="button"
                                        onClick={() => setActiveLessonId(l.id)}
                                        className="w-full flex items-center gap-2 p-2 text-left"
                                        data-testid={`lesson-select-${l.id}`}
                                    >
                                        <span className={`w-6 h-6 shrink-0 rounded-md grid place-items-center text-[10px] font-bold ${meta.tone}`}>
                                            <meta.Icon size={11} weight="fill" />
                                        </span>
                                        <span className="flex-1 min-w-0 text-xs font-semibold truncate">{l.title}</span>
                                    </button>
                                    {isActive && !readOnly && (
                                        <div className="flex items-center gap-1 px-2 pb-2">
                                            <MiniBtn onClick={() => moveLesson(idx, -1)} disabled={idx === 0} testid={`lesson-up-${l.id}`}><CaretUp size={10} weight="bold" /></MiniBtn>
                                            <MiniBtn onClick={() => moveLesson(idx, 1)} disabled={idx === lessons.length - 1} testid={`lesson-down-${l.id}`}><CaretDown size={10} weight="bold" /></MiniBtn>
                                            <MiniBtn onClick={() => removeLesson(l)} tone="danger" testid={`lesson-delete-${l.id}`}><Trash size={10} weight="bold" /></MiniBtn>
                                        </div>
                                    )}
                                </div>
                            );
                        })
                    )}
                </div>

                {/* Editor content */}
                <div className="flex-1 overflow-y-auto p-6 min-w-0">
                    {activeLesson ? (
                        <LessonForm
                            key={activeLesson.id}
                            lesson={activeLesson}
                            readOnly={readOnly}
                            course={course}
                            onSave={(patch) => persistLesson(activeLesson.id, patch)}
                            onLessonRefresh={applyLessonRefresh}
                        />
                    ) : (
                        <div className="flex items-center justify-center h-full">
                            <div className="text-center text-muted-foreground">
                                <WarningCircle size={30} weight="duotone" className="mx-auto mb-2" />
                                <div className="text-sm font-semibold">No lesson selected</div>
                                <p className="text-xs mt-1">Pick a lesson from the list, or add one using the toolbar.</p>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}


function MiniBtn({ children, onClick, disabled, tone, testid }) {
    const toneCls = tone === "danger"
        ? "text-rose-600 hover:bg-rose-50"
        : "text-muted-foreground hover:text-foreground hover:bg-white";
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            data-testid={testid}
            className={`w-6 h-6 grid place-items-center rounded border border-border ${toneCls} transition-colors disabled:opacity-40 disabled:cursor-not-allowed`}
        >
            {children}
        </button>
    );
}


function LessonForm({ lesson, readOnly, course, onSave, onLessonRefresh }) {
    const [title, setTitle] = useState(lesson.title || "");
    const [videoUrl, setVideoUrl] = useState(lesson.video_url || "");
    const [textBody, setTextBody] = useState(lesson.text_body || "");
    const [fileMediaId, setFileMediaId] = useState(lesson.file_media_id || null);
    const [durationMinutes, setDurationMinutes] = useState(
        lesson.duration_minutes == null ? "" : String(lesson.duration_minutes),
    );
    const [showNarrationForText, setShowNarrationForText] = useState(!!lesson.narration_media_path);
    const [saving, setSaving] = useState(false);

    // Reset when switching lessons — intentionally keyed only on `lesson.id`
    // so we don't clobber the user's in-flight edits on every prop change.
    useEffect(() => {
        setTitle(lesson.title || "");
        setVideoUrl(lesson.video_url || "");
        setTextBody(lesson.text_body || "");
        setFileMediaId(lesson.file_media_id || null);
        setDurationMinutes(lesson.duration_minutes == null ? "" : String(lesson.duration_minutes));
        setShowNarrationForText(!!lesson.narration_media_path);
    }, [lesson.id]);

    const currentDuration = lesson.duration_minutes == null ? "" : String(lesson.duration_minutes);
    const dirty =
        title !== (lesson.title || "") ||
        videoUrl !== (lesson.video_url || "") ||
        textBody !== (lesson.text_body || "") ||
        fileMediaId !== (lesson.file_media_id || null) ||
        durationMinutes !== currentDuration;

    const onSaveClick = async () => {
        const patch = {};
        if (title !== (lesson.title || "")) patch.title = title.trim();
        if (lesson.type === "video" && videoUrl !== (lesson.video_url || "")) patch.video_url = videoUrl.trim();
        if ((lesson.type === "text" || lesson.type === "narrated") && textBody !== (lesson.text_body || "")) patch.text_body = textBody;
        if (lesson.type === "file"  && fileMediaId !== (lesson.file_media_id || null)) patch.file_media_id = fileMediaId;
        if (durationMinutes !== currentDuration) {
            patch.duration_minutes = durationMinutes === "" ? null : Math.max(0, Math.min(600, Number(durationMinutes)));
        }
        if (!Object.keys(patch).length) return;
        try {
            setSaving(true);
            await onSave(patch);
            toast.success("Lesson saved");
        } finally {
            setSaving(false);
        }
    };

    const meta = TYPE_META[lesson.type] || TYPE_META.text;

    return (
        <div className="max-w-3xl xl:max-w-4xl mx-auto space-y-5" data-testid="lesson-form">
            {/* Type badge */}
            <div className="flex items-center justify-between gap-3">
                <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full border text-[10px] font-bold uppercase tracking-wider ${meta.tone}`}>
                    <meta.Icon size={11} weight="fill" /> {meta.label} lesson
                </span>
            </div>

            {/* Title */}
            <label className="block">
                <span className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Lesson title</span>
                <Input
                    disabled={readOnly}
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="What will the learner see for this step?"
                    className="mt-1"
                    maxLength={140}
                    data-testid="lesson-title-input"
                />
            </label>

            {/* Estimated duration — feeds the auto-computed Course Setup duration */}
            {lesson.type !== "quiz" && (
                <label className="block max-w-xs">
                    <span className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground inline-flex items-center gap-1">
                        <Clock size={11} weight="duotone" /> Estimated duration (minutes)
                    </span>
                    <Input
                        disabled={readOnly}
                        type="number"
                        min="0"
                        max="600"
                        step="1"
                        value={durationMinutes}
                        onChange={(e) => setDurationMinutes(e.target.value)}
                        placeholder="e.g. 8"
                        className="mt-1"
                        data-testid="lesson-duration-input"
                    />
                    <p className="text-[11px] text-muted-foreground mt-1">
                        Rolls up into the course&apos;s total duration. Leave blank if unsure.
                    </p>
                </label>
            )}

            {/* Type-specific body */}
            {lesson.type === "video" && (
                <label className="block">
                    <span className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">
                        Video URL <span className="text-muted-foreground normal-case tracking-normal">— YouTube / Vimeo / Loom / Google Drive</span>
                    </span>
                    <Input
                        disabled={readOnly}
                        value={videoUrl}
                        onChange={(e) => setVideoUrl(e.target.value)}
                        placeholder="https://youtube.com/watch?v=…"
                        className="mt-1"
                        data-testid="lesson-video-url"
                    />
                    <p className="text-[11px] text-muted-foreground mt-1">
                        Paste a shareable link. We recommend unlisted/private videos so the content stays inside the Academy.
                    </p>
                </label>
            )}

            {lesson.type === "text" && (
                <label className="block">
                    <span className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">
                        Lesson content
                    </span>
                    <div className="mt-1">
                        <RichTextEditor
                            value={textBody}
                            onChange={setTextBody}
                            placeholder="Write out the lesson body. Use the toolbar for headings, lists, and emphasis."
                            disabled={readOnly}
                        />
                    </div>
                    <input type="hidden" data-testid="lesson-text-body" value={textBody} readOnly />
                </label>
            )}

            {/* iter100.157n — Narrated Slide lesson: rich slide body + mandatory narration */}
            {lesson.type === "narrated" && (
                <>
                    <label className="block">
                        <span className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground inline-flex items-center gap-1">
                            <Article size={11} weight="duotone" /> Slide content <span className="text-muted-foreground normal-case tracking-normal">— rendered above the audio player</span>
                        </span>
                        <div className="mt-1">
                            <RichTextEditor
                                value={textBody}
                                onChange={setTextBody}
                                placeholder="What appears on the slide while the narration plays."
                                disabled={readOnly}
                            />
                        </div>
                        <input type="hidden" data-testid="lesson-text-body" value={textBody} readOnly />
                    </label>
                    <NarrationEditor
                        lesson={lesson}
                        readOnly={readOnly}
                        variant="full"
                        onLessonRefresh={onLessonRefresh}
                    />
                </>
            )}

            {/* iter100.157n — Optional narration for Text lessons */}
            {lesson.type === "text" && (
                <div>
                    {showNarrationForText || lesson.narration_media_path ? (
                        <NarrationEditor
                            lesson={lesson}
                            readOnly={readOnly}
                            variant="optional"
                            onLessonRefresh={onLessonRefresh}
                        />
                    ) : (
                        !readOnly && (
                            <button
                                type="button"
                                onClick={() => setShowNarrationForText(true)}
                                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-dashed border-sky-300 bg-sky-50/40 hover:bg-sky-50 text-sky-700 text-xs font-semibold"
                                data-testid={`add-narration-${lesson.id}`}
                            >
                                <SpeakerHigh size={13} weight="fill" /> Add optional voice-over narration
                            </button>
                        )
                    )}
                </div>
            )}

            {lesson.type === "file" && (
                <FileLessonUploader
                    value={fileMediaId}
                    onChange={setFileMediaId}
                    courseId={course.id}
                    readOnly={readOnly}
                />
            )}

            {lesson.type === "quiz" && (
                <QuizBuilder lesson={lesson} readOnly={readOnly} />
            )}

            {/* Save bar (not shown for quiz — QuizBuilder saves itself) */}
            {lesson.type !== "quiz" && !readOnly && (
                <div className="flex items-center justify-end gap-2 pt-2 border-t border-border">
                    <Button
                        onClick={onSaveClick}
                        disabled={saving || !dirty || !title.trim()}
                        data-testid="lesson-save-btn"
                    >
                        <FloppyDisk size={13} weight="fill" className="mr-1.5" />
                        {saving ? "Saving…" : dirty ? "Save lesson" : "Saved"}
                    </Button>
                </div>
            )}
        </div>
    );
}


function FileLessonUploader({ value, onChange, courseId, readOnly }) {
    const [uploading, setUploading] = useState(false);
    const [fileMeta, setFileMeta] = useState(null);
    const inputRef = React.useRef(null);

    // Load meta for existing media_id (name + size) — pull from a lightweight fetch
    useEffect(() => {
        setFileMeta(null);
        if (!value) return undefined;
        // We don't have a media-metadata endpoint, so just render the ID pill.
        setFileMeta({ id: value });
        return undefined;
    }, [value]);

    const onPick = () => inputRef.current?.click();

    const onFile = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        if (f.size > 50 * 1024 * 1024) {
            toast.error("Max 50 MB for file lessons.");
            e.target.value = "";
            return;
        }
        const form = new FormData();
        form.append("file", f);
        form.append("kind", "file");
        if (courseId) form.append("course_id", courseId);
        try {
            setUploading(true);
            const { data } = await api.post("/course-builder/media/upload", form, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            onChange?.(data.id);
            setFileMeta({ id: data.id, name: data.original_filename, size: data.size });
            toast.success("File uploaded");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Upload failed");
        } finally {
            setUploading(false);
            e.target.value = "";
        }
    };

    return (
        <div>
            <span className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">
                Attached file <span className="text-muted-foreground normal-case tracking-normal">— PDF / DOC / Slides · max 50 MB</span>
            </span>
            <div className="mt-1">
                <input ref={inputRef} type="file" accept=".pdf,.doc,.docx,.ppt,.pptx,.txt,.md" hidden onChange={onFile} data-testid="lesson-file-input" />
                {fileMeta ? (
                    <div className="flex items-center gap-3 p-3 bg-secondary/40 border border-border rounded-xl" data-testid="lesson-file-preview">
                        <FileArrowUp size={20} weight="duotone" className="text-primary shrink-0" />
                        <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold truncate">{fileMeta.name || "Attached file"}</div>
                            <div className="text-[11px] text-muted-foreground">
                                {fileMeta.size ? `${Math.round(fileMeta.size / 1024)} KB` : `ID: ${fileMeta.id.slice(0, 8)}…`}
                            </div>
                        </div>
                        {!readOnly && (
                            <>
                                <Button size="sm" variant="outline" onClick={onPick} disabled={uploading} data-testid="lesson-file-replace">
                                    Replace
                                </Button>
                                <Button size="sm" variant="outline" onClick={() => onChange?.(null)} disabled={uploading} data-testid="lesson-file-remove">
                                    Remove
                                </Button>
                            </>
                        )}
                    </div>
                ) : (
                    <button
                        type="button"
                        onClick={onPick}
                        disabled={readOnly || uploading}
                        className="w-full h-24 rounded-xl border-2 border-dashed border-border hover:border-primary hover:bg-primary/5 transition-colors flex flex-col items-center justify-center gap-1 text-muted-foreground hover:text-primary disabled:cursor-not-allowed"
                        data-testid="lesson-file-upload-btn"
                    >
                        <FileArrowUp size={22} weight="duotone" />
                        <span className="text-xs font-semibold">{uploading ? "Uploading…" : "Click to upload file"}</span>
                    </button>
                )}
            </div>
        </div>
    );
}
